import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';

/**
 * Judge status constants — matches the main app's JUDGE_STATUS.
 */
export const JUDGE_STATUS = {
  NOT_GENERATED: 'not_generated',
  GENERATED: 'generated',
  READY: 'ready',
  DONE: 'done'
} as const;

export type JudgeStatusType = (typeof JUDGE_STATUS)[keyof typeof JUDGE_STATUS];

export interface AdjudicatorStatus {
  adjudicatorUuid: string;
  letter: string;
  status: JudgeStatusType;
}

export interface TtItemJudgesStatus {
  ttItemUuid: string;
  adjudicators: AdjudicatorStatus[];
}

export type JudgesStatusPayload = TtItemJudgesStatus[];

interface JudgesStatusState {
  /** Status data indexed by ttItemUuid for O(1) lookup */
  byTtItemUuid: Record<string, AdjudicatorStatus[]>;
}

const initialState: JudgesStatusState = {
  byTtItemUuid: {}
};

const judgesStatusSlice = createSlice({
  name: 'judgesStatus',
  initialState,
  reducers: {
    setJudgesStatus: (state, action: PayloadAction<JudgesStatusPayload>) => {
      // Replace all status data with the new payload
      const newMap: Record<string, AdjudicatorStatus[]> = {};
      action.payload.forEach((item) => {
        newMap[item.ttItemUuid] = item.adjudicators;
      });
      state.byTtItemUuid = newMap;
    },
    clearJudgesStatus: (state) => {
      state.byTtItemUuid = {};
    }
  }
});

export const { setJudgesStatus, clearJudgesStatus } =
  judgesStatusSlice.actions;

/** Select all judges status data */
export const selectJudgesStatusMap = (state: RootState) =>
  state.judgesStatus?.byTtItemUuid || {};

/** Select judges status for a specific timetable item */
export const selectJudgesStatusForTtItem = (
  state: RootState,
  ttItemUuid: string
): AdjudicatorStatus[] | undefined =>
  state.judgesStatus?.byTtItemUuid?.[ttItemUuid];

export default judgesStatusSlice.reducer;
